import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileText, X } from "lucide-react";
import { LogoMockup } from "./LogoMockup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT =
  ".pdf,.jpg,.jpeg,.png,.webp,.svg,application/pdf,image/jpeg,image/png,image/webp,image/svg+xml";

const labelCls =
  "block font-mono text-[10px] uppercase tracking-[0.2em] text-mutedink mb-2";
const inputCls =
  "w-full bg-white border border-ink/15 px-4 py-3 text-sm text-ink placeholder:text-mutedink/60 focus:outline-none focus:border-oxblood transition-colors duration-300";

export const EMPTY_LOGO = {
  enabled: false,
  schoolName: "",
  file: null, // { logo_path, logo_filename, logo_content_type }
  previewUrl: null,
  isPdf: false,
  accepted: false,
};

/**
 * Free stitched school-logo option for Maison Piqué polos.
 * Uploads the file immediately to /api/orders/logo and keeps the returned path.
 */
export const SchoolLogoOption = ({ logo, setLogo }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const update = (patch) => setLogo((l) => ({ ...l, ...patch }));

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large", {
        description: "Please keep the logo under 10 MB.",
      });
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/orders/logo`, fd);
      const isPdf = f.type === "application/pdf";
      update({
        file: data,
        previewUrl: isPdf ? null : URL.createObjectURL(f),
        isPdf,
      });
    } catch (err) {
      toast.error("Could not upload the logo", {
        description:
          err.response?.data?.detail ||
          "Please try a PDF, JPG, PNG, WEBP or SVG.",
      });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div
      data-testid="school-logo-option"
      className="border border-ink/15 bg-white/60 p-4"
    >
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          data-testid="school-logo-checkbox"
          checked={logo.enabled}
          onChange={(e) => update({ enabled: e.target.checked })}
          className="mt-1 accent-[#7A1F2E]"
        />
        <span>
          <span className="block text-sm text-ink">
            Add your school logo —{" "}
            <span className="text-oxblood">stitched on for free</span>
          </span>
          <span className="block mt-0.5 text-xs text-mutedink leading-relaxed">
            Embroidered on the left chest. Upload the school&apos;s logo as a
            PDF or image.
          </span>
        </span>
      </label>

      {logo.enabled && (
        <div className="mt-4 space-y-4 pl-7">
          <div>
            <label htmlFor="school-name" className={labelCls}>
              School name
            </label>
            <input
              id="school-name"
              data-testid="school-name-input"
              required
              value={logo.schoolName}
              onChange={(e) => update({ schoolName: e.target.value })}
              placeholder="Crescent Heights Academy"
              className={inputCls}
            />
          </div>

          <div>
            <p className={labelCls}>School logo file (PDF preferred)</p>
            {logo.file ? (
              <div
                data-testid="school-logo-file"
                className="flex items-center justify-between gap-3 border border-ink/15 bg-white px-4 py-3"
              >
                <span className="inline-flex items-center gap-2 text-sm text-ink truncate">
                  <FileText className="w-4 h-4 text-oxblood shrink-0" />
                  <span className="truncate">{logo.file.logo_filename}</span>
                </span>
                <button
                  type="button"
                  data-testid="school-logo-remove"
                  onClick={() =>
                    update({ file: null, previewUrl: null, isPdf: false })
                  }
                  className="text-mutedink hover:text-oxblood"
                  aria-label="Remove file"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label
                data-testid="school-logo-dropzone"
                className="flex items-center justify-center gap-2 border border-dashed border-ink/25 bg-white px-4 py-5 text-sm text-mutedink hover:border-oxblood hover:text-oxblood cursor-pointer transition-colors duration-300"
              >
                <Upload className="w-4 h-4" />
                {uploading ? "Uploading…" : "Choose PDF or image"}
                <input
                  ref={inputRef}
                  type="file"
                  data-testid="school-logo-input"
                  accept={ACCEPT}
                  onChange={onFile}
                  disabled={uploading}
                  className="sr-only"
                />
              </label>
            )}
          </div>

          {logo.file && (
            <div>
              <p className={labelCls}>Placement preview</p>
              <LogoMockup
                previewUrl={logo.previewUrl}
                isPdf={logo.isPdf}
                schoolName={logo.schoolName}
              />
            </div>
          )}

          <label
            data-testid="school-logo-disclaimer"
            className="flex items-start gap-3 border border-oxblood/30 bg-parchment p-3 cursor-pointer"
          >
            <input
              type="checkbox"
              data-testid="school-logo-accept"
              required
              checked={logo.accepted}
              onChange={(e) => update({ accepted: e.target.checked })}
              className="mt-0.5 accent-[#7A1F2E]"
            />
            <span className="text-xs text-[#2A2A2A] leading-relaxed">
              <strong>Disclaimer.</strong> By uploading a logo you confirm you
              have the school&apos;s permission to reproduce it. Hailey and
              Chloe Holdings Inc. stitches the artwork as supplied and is{" "}
              <strong>not responsible for any potential liability</strong>,
              including trademark, copyright or permission issues, arising from
              its use.
            </span>
          </label>
        </div>
      )}
    </div>
  );
};
